'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

const stats = [
  { value: 25, suffix: '+', label: 'Years of Excellence' },
  { value: 16, suffix: '', label: 'Countries Served' },
  { value: 50000, suffix: '+', label: 'Active Clients' },
];

interface Office {
  country: string;
  city: string;
  code: string;
  flag: string;
  hq?: boolean;
}

interface Region {
  name: string;
  offices: Office[];
}

const regions: Region[] = [
  {
    name: 'Headquarters',
    offices: [
      { country: 'Bulgaria', city: 'Sofia', code: 'BG', flag: '🇧🇬', hq: true },
    ],
  },
  {
    name: 'Balkans & Eastern Europe',
    offices: [
      { country: 'Serbia', city: 'Belgrade', code: 'RS', flag: '🇷🇸' },
      { country: 'Croatia', city: 'Zagreb', code: 'HR', flag: '🇭🇷' },
      { country: 'Slovenia', city: 'Koper', code: 'SI', flag: '🇸🇮' },
      { country: 'Greece', city: 'Athens', code: 'GR', flag: '🇬🇷' },
      { country: 'North Macedonia', city: 'Skopje', code: 'MK', flag: '🇲🇰' },
      { country: 'Romania', city: 'Bucharest', code: 'RO', flag: '🇷🇴' },
      { country: 'Kosovo', city: 'Pristina', code: 'XK', flag: '🇽🇰' },
      { country: 'Albania', city: 'Tirana', code: 'AL', flag: '🇦🇱' },
      { country: 'Bosnia & Herzegovina', city: 'Sarajevo', code: 'BA', flag: '🇧🇦' },
    ],
  },
  {
    name: 'Asia',
    offices: [
      { country: 'China', city: 'Shanghai', code: 'CN', flag: '🇨🇳' },
      { country: 'Taiwan', city: 'Taipei', code: 'TW', flag: '🇹🇼' },
      { country: 'Vietnam', city: 'Ho Chi Minh City', code: 'VN', flag: '🇻🇳' },
    ],
  },
  {
    name: 'Western Europe & Americas',
    offices: [
      { country: 'Germany', city: 'Munich', code: 'DE', flag: '🇩🇪' },
      { country: 'USA', city: 'New York', code: 'US', flag: '🇺🇸' },
    ],
  },
];

// Map GlobalPresence country names → exact contacts page country names
const countryNameMap: Record<string, string> = {
  'Bulgaria': 'Bulgaria',
  'Serbia': 'Serbia',
  'Croatia': 'Croatia',
  'Slovenia': 'Slovenia',
  'Greece': 'Greece',
  'North Macedonia': 'North Macedonia',
  'Romania': 'Romania',
  'Kosovo': 'Kosovo',
  'Albania': 'Albania',
  'Bosnia & Herzegovina': 'Bosnia and Herzegovina',
  'China': 'China',
  'Taiwan': 'Taiwan',
  'Vietnam': 'Vietnam',
  'Germany': 'Germany',
  'USA': 'USA',
};

const GlobalPresenceSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [countersStarted, setCountersStarted] = useState(false);
  const [counts, setCounts] = useState(stats.map(() => 0));

  useEffect(() => {
    const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    if (!els) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !countersStarted) {
          setCountersStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [countersStarted]);

  useEffect(() => {
    if (!countersStarted) return;
    stats.forEach((stat, i) => {
      const duration = 1400;
      const steps = 50;
      const increment = stat.value / steps;
      let current = 0;
      let step = 0;
      const timer = setInterval(() => {
        step++;
        current = Math.min(Math.round(increment * step), stat.value);
        setCounts((prev) => {
          const next = [...prev];
          next[i] = current;
          return next;
        });
        if (step >= steps) clearInterval(timer);
      }, duration / steps);
    });
  }, [countersStarted]);

  return (
    <section id="company" className="relative py-20 lg:py-28 overflow-hidden" ref={sectionRef} style={{ background: '#FFFFFF' }}>
      {/* Ambient blob */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full blob pointer-events-none" style={{ filter: 'blur(120px)', background: 'rgba(27,107,138,0.07)' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="reveal text-center mb-16">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-body mb-3" style={{ color: '#1B6B8A' }}>
            <span className="w-6 h-px" style={{ background: '#1B6B8A' }} />
            Global Network
            <span className="w-6 h-px" style={{ background: '#1B6B8A' }} />
          </span>
          <h2 className="font-display text-4xl lg:text-5xl leading-tight" style={{ color: '#0F2340' }}>
            Connecting your cargo to
            <br />
            <span className="italic" style={{ color: '#1B6B8A' }}>every corner of the world.</span>
          </h2>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className="reveal rounded-2xl p-6 text-center border transition-colors duration-300"
              style={{ background: '#F4F6F9', borderColor: 'rgba(27,107,138,0.2)', boxShadow: '0 2px 16px rgba(15,35,64,0.07)', transitionDelay: `${i * 80}ms` }}
            >
              <p className="font-display text-4xl lg:text-5xl mb-1" style={{ color: '#0F2340' }}>
                {counts[i]}<span style={{ color: '#1B6B8A' }}>{stat.suffix}</span>
              </p>
              <p className="text-sm font-body" style={{ color: '#3A5070' }}>{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Description */}
        <div className="reveal-left max-w-2xl mb-12">
          <h3 className="font-display text-2xl lg:text-3xl mb-4 leading-snug" style={{ color: '#0F2340' }}>
            Offices in 16 countries,<br />one standard of service.
          </h3>
          <p className="font-body leading-relaxed text-base" style={{ color: '#3A5070' }}>
            From our headquarters in Bulgaria to offices across the Balkans, Eastern Europe, Asia, and the Americas — WTO brings local expertise with global reach. Our network spans every major trade route.
          </p>
        </div>

        {/* Region-based office grid */}
        <div className="reveal space-y-8 mb-12">
          {regions.map((region) => (
            <div key={region.name}>
              {/* Region label */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs uppercase tracking-widest font-body font-semibold" style={{ color: '#1B6B8A' }}>
                  {region.name}
                </span>
                <span className="flex-1 h-px" style={{ background: 'rgba(27,107,138,0.2)' }} />
              </div>
              {/* Office cards */}
              <div className="flex flex-wrap gap-3">
                {region.offices.map((office) => (
                  <Link
                    key={office.country}
                    href={`/contacts?country=${encodeURIComponent(countryNameMap[office.country] ?? office.country)}`}
                    className="group flex items-center gap-2.5 px-4 py-2.5 rounded-xl border transition-all duration-200 cursor-pointer"
                    style={{
                      background: office.hq ? 'rgba(27,107,138,0.1)' : '#F4F6F9',
                      borderColor: office.hq ? 'rgba(27,107,138,0.4)' : 'rgba(15,35,64,0.12)',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(27,107,138,0.12)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(27,107,138,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = office.hq ? 'rgba(27,107,138,0.1)' : '#F4F6F9';
                      (e.currentTarget as HTMLElement).style.borderColor = office.hq ? 'rgba(27,107,138,0.4)' : 'rgba(15,35,64,0.12)';
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      <span
                        className="text-xs font-bold px-1.5 py-0.5 rounded"
                        style={{
                          background: '#1B6B8A',
                          color: '#ffffff',
                          letterSpacing: '0.05em',
                          border: '1px solid #1B6B8A',
                        }}
                      >
                        {office.hq ? 'BG' : office.code}
                      </span>
                      <p className="text-sm font-semibold font-body leading-tight" style={{ color: '#0F2340' }}>
                        {office.country}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA buttons */}
        <div className="reveal-left flex flex-col sm:flex-row gap-3">
          <a
            href="/transport-quote"
            className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-semibold font-body transition-all duration-200 hover:opacity-90 self-start"
            style={{ background: '#1A5FAB', color: '#ffffff' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 0 0 2-2V5a2 2 0 0-2-2H7a2 2 0 0-2 2v14a2 2 0 0 2 2z" />
            </svg>
            Request a Quote
          </a>
          <a
            href="/contacts"
            className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-semibold font-body transition-all duration-200 hover:opacity-90 self-start"
            style={{ background: '#1A5FAB', color: '#ffffff' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 0 1-2.827 0l-4.244-4.243a8 8 0 1 1 11.314 0z" />
              <path d="M15 11a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
            </svg>
            Find your nearest office
          </a>
        </div>
      </div>
    </section>
  );
};

export default GlobalPresenceSection;